import * as Yup from 'yup';
import { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

// @mui
import {
  Alert,
  Box,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  Stack
} from '@mui/material';

import { LoadingButton } from '@mui/lab';

// routes
import { PATH_AUTH } from 'src/routes/paths';
// hooks
import useAuth from 'src/hooks/useAuth';
// portal <-> account-type mapping
import { PORTAL_TYPES } from 'src/contexts/JWTContext';
// components
import Icon from 'src/components/universe/Icon';
import FormProvider from 'src/components/hook-form/FormProvider';
import RHFTextField from 'src/components/hook-form/RHFTextField';
import {
  AUTH_SHORTER,
  authFieldSx,
  authButtonSx,
  authDividerSx,
  authLinkSx
} from 'src/layouts/auth/portals';
import MicrosoftButton from '../MicrosoftButton';

// ----------------------------------------------------------------------
// THE INTRANET SIGN-IN: two ways in, one card (DESIGN.md §26 The sign-in
// card).
//
// MICROSOFT FIRST. The team works in Microsoft 365, so for most people the
// whole sign-in is one press — it leads, as the single-sign-on way in leads on
// every enterprise sign-in. The email and password follow under a divider
// that names them, with their own primary action, so the form keeps the one
// filled button a surface may have (§17) and nobody reads the Microsoft
// button as a second Submit. Both ways in report into ONE notice at the top
// of the card: a failure is read where the eye starts, whichever way it came.
//
// The two quiet ways round a password have each moved to where they are
// looked for: "Forgot password?" onto the password's own label line, and the
// emailed sign-in link under the button, as the alternative to it.
// ----------------------------------------------------------------------

export default function LoginForm() {
  const { signInWithPassword, signInWithMicrosoft, clearSsoError, sso } =
    useAuth();
  const { pathname, search } = useLocation();

  const [showPassword, setShowPassword] = useState(false);
  const [authError, setAuthError] = useState('');
  const [wrongPortal, setWrongPortal] = useState(null);

  // A page the browser restores from its back-forward cache — Back from
  // Microsoft's own sign-in — comes back frozen as it was left: "Redirecting
  // to Microsoft…" over a disabled form. Put it back to rest.
  useEffect(() => {
    const onShow = (event) => {
      if (event.persisted) clearSsoError?.();
    };
    window.addEventListener('pageshow', onShow);
    return () => window.removeEventListener('pageshow', onShow);
  }, [clearSsoError]);

  const LoginSchema = Yup.object().shape({
    email: Yup.string()
      .email('Email must be a valid email address')
      .required('Email is required'),
    password: Yup.string().required('Password is required')
  });

  const defaultValues = {
    email: '',
    password: ''
  };

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues
  });

  const {
    handleSubmit,
    formState: { isSubmitting }
  } = methods;

  const ssoStatus = sso?.status || 'idle';
  // leaving for Microsoft, or back from it and not yet resolved: the card
  // holds still rather than letting a second sign-in start under the first
  const ssoBusy = ssoStatus === 'redirecting' || ssoStatus === 'returning';

  const onSubmit = async (data) => {
    setAuthError('');
    setWrongPortal(null);
    if (sso?.error) clearSsoError();
    try {
      await signInWithPassword(
        data.email,
        data.password,
        PORTAL_TYPES.intranet
      );
      // On success, AuthProvider's onAuthStateChange updates the session and
      // the guards route the user onward — no manual navigation needed.
    } catch (err) {
      // Supabase answers in its own words ("Email not confirmed", "Invalid
      // login credentials"); say what happened and what to do instead.
      const said = err?.message || '';
      const message = /not confirmed/i.test(said)
        ? 'Confirm your email address first — check your inbox for the link we sent when the account was created.'
        : /invalid login credentials/i.test(said)
        ? 'That email and password do not match. Check them and try again, or use Forgot password.'
        : said || 'Invalid email or password';
      setAuthError(message);
      // Valid credentials for another portal: offer the way there.
      if (err?.portalPath) {
        setAuthError(err.message);
        setWrongPortal({ path: err.portalPath, label: err.portalLabel });
      }
    }
  };

  const onMicrosoft = async () => {
    setAuthError('');
    setWrongPortal(null);
    try {
      // back to THIS page: a link to a record opened while signed out still
      // opens that record once Microsoft has said yes
      await signInWithMicrosoft({
        portal: 'intranet',
        returnTo: `${pathname}${search}`
      });
    } catch {
      // the context has put the reason into `sso.error`, shown below
    }
  };

  // One notice for every reason a sign-in did not happen: the password
  // form's own, or the way back from Microsoft.
  const ssoPortal = sso?.error?.portalPath
    ? { path: sso.error.portalPath, label: sso.error.portalLabel }
    : null;
  const notice = authError
    ? {
        severity: wrongPortal ? 'info' : 'error',
        message: authError,
        portal: wrongPortal
      }
    : sso?.error
    ? {
        severity: sso.error.severity || 'error',
        message: sso.error.message,
        portal: ssoPortal
      }
    : null;

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3} useFlexGap sx={{ [AUTH_SHORTER]: { gap: 2.5 } }}>
        {notice && (
          <Alert
            severity={notice.severity}
            action={
              notice.portal ? (
                <Link
                  component={RouterLink}
                  to={notice.portal.path}
                  underline="hover"
                  sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
                >
                  Go to {notice.portal.label}
                </Link>
              ) : null
            }
          >
            {notice.message}
          </Alert>
        )}

        <MicrosoftButton
          onClick={onMicrosoft}
          loading={ssoBusy}
          loadingLabel={
            ssoStatus === 'returning'
              ? 'Signing you in…'
              : 'Redirecting to Microsoft…'
          }
          disabled={isSubmitting}
        />

        <Divider sx={authDividerSx}>or sign in with email</Divider>

        <Stack spacing={2.5} useFlexGap sx={{ [AUTH_SHORTER]: { gap: 2 } }}>
          <RHFTextField
            sx={authFieldSx}
            name="email"
            label="Email address"
            autoComplete="username"
            disabled={ssoBusy}
            inputProps={{
              inputMode: 'email',
              autoCapitalize: 'none',
              autoCorrect: 'off',
              spellCheck: 'false'
            }}
          />

          <Box sx={{ position: 'relative' }}>
            <RHFTextField
              name="password"
              sx={authFieldSx}
              label="Password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              disabled={ssoBusy}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      aria-label={
                        showPassword ? 'Hide password' : 'Show password'
                      }
                      aria-pressed={showPassword}
                    >
                      <Icon
                        name={showPassword ? 'Eye' : 'EyeSlash'}
                        size="md"
                      />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            {/* The way back in when the password is the problem, on the
                password's own label line — after the field in the document,
                so the tab order is field, eye, then this. */}
            <Link
              component={RouterLink}
              to={PATH_AUTH.resetPassword}
              underline="hover"
              sx={(theme) => ({
                ...authLinkSx(theme, { quiet: true }),
                position: 'absolute',
                top: 0,
                right: 0
              })}
            >
              Forgot password?
            </Link>
          </Box>
        </Stack>

        <LoadingButton
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          loading={isSubmitting}
          disabled={ssoBusy}
          sx={authButtonSx}
        >
          <span className="notranslate" translate="no">
            Sign in
          </span>
        </LoadingButton>
      </Stack>

      {/* The other way round a password has its own page, so this stays a
          plain link: one primary action on the card, and no flow sharing
          state with the password form. */}
      <Box sx={{ mt: 2.5, [AUTH_SHORTER]: { mt: 2 }, textAlign: 'center' }}>
        <Link
          component={RouterLink}
          to={PATH_AUTH.magicLink}
          underline="hover"
          sx={authLinkSx}
        >
          Email me a sign-in link instead
        </Link>
      </Box>
    </FormProvider>
  );
}
